/**
 * RolloutProgress — a grpo run's rollouts per optimizer step (TRA-1377).
 * The live step reads as three numbers and a bar: rollouts finished, the
 * trainable ones and how many the step needs. Below it the run's steps
 * against a dashed line at what a step needs: up to BAR_STEPS steps as
 * stacked bars, trainable rollouts in ink under the finished rollouts the
 * trainer could not learn from; past that as two lines on a step axis, as the
 * reward curve draws a long run, since hundreds of bars are slivers.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type {
  TrainingRolloutProgress,
  TrainingRunDocument,
  TrainingStepRollouts,
} from '@/api/generated/model'
import { CHART_INK, CHART_REFERENCE_DASH, CHART_REFERENCE_STROKE } from '@/components/evals/chartStyle'
import { CardLabel } from '@/components/training/HealthCards'
import { liveStep, type RolloutRow, rolloutRows, stepTicks } from '@/components/training/progress'

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }
/** The most steps a run may have to be drawn as bars; a longer one is drawn as lines throughout. */
const BAR_STEPS = 40
const NEEDED_MARK = { mark: <span className="w-4 border-t border-dashed border-foreground" />, label: 'Needed' }

const BAR_LEGEND = [
  { mark: <span className="h-2.5 w-2.5 bg-foreground" />, label: 'Trainable' },
  {
    mark: <span className="h-2.5 w-2.5" style={{ backgroundColor: CHART_INK, opacity: 0.35 }} />,
    label: 'Not trainable',
  },
  NEEDED_MARK,
]

const LINE_LEGEND = [
  { mark: <span className="h-[2px] w-4 bg-foreground" />, label: 'Trainable' },
  { mark: <span className="h-px w-4" style={{ backgroundColor: CHART_INK }} />, label: 'Done' },
  NEEDED_MARK,
]

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <CardLabel>{label}</CardLabel>
      <p className="mt-2 font-mono text-2xl tabular-nums">{value}</p>
    </div>
  )
}

function LiveStep({ step, needed }: { step: TrainingStepRollouts; needed: number }): React.JSX.Element {
  const share = Math.min(1, step.trainable / needed)
  return (
    <div className="space-y-4 border border-border p-5">
      <CardLabel>Step {step.step.toLocaleString()} in progress</CardLabel>
      <div className="grid grid-cols-3 gap-6">
        <Stat label="Rollouts done" value={step.done.toLocaleString()} />
        <Stat label="Needed" value={needed.toLocaleString()} />
        <Stat label="Trainable" value={step.trainable.toLocaleString()} />
      </div>
      <div
        role="progressbar"
        aria-label="Trainable rollouts for this step"
        aria-valuemin={0}
        aria-valuemax={needed}
        aria-valuenow={Math.min(step.trainable, needed)}
        className="h-1.5 w-full border border-border"
      >
        <div className="h-full bg-foreground" style={{ width: `${(share * 100).toFixed(1)}%` }} />
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        A rollout is trainable when its whole group finished and the group's rewards differ.
        {step.cancelled > 0 && ` ${step.cancelled.toLocaleString()} rollouts were cancelled.`}
      </p>
    </div>
  )
}

function StepTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
}): React.JSX.Element | null {
  const row = payload?.[0]?.payload as RolloutRow | undefined
  if (!active || !row) return null
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        Step {row.step.toLocaleString()}
      </p>
      <p className="mt-1">
        Done: <span className="font-mono tabular-nums">{(row.trainable + row.untrainable).toLocaleString()}</span>
      </p>
      <p className="mt-1">
        Trainable: <span className="font-mono tabular-nums">{row.trainable.toLocaleString()}</span>
      </p>
      {row.cancelled > 0 && (
        <p className="mt-1">
          Cancelled: <span className="font-mono tabular-nums">{row.cancelled.toLocaleString()}</span>
        </p>
      )}
    </div>
  )
}

function StepBars({ rows, needed }: { rows: RolloutRow[]; needed: number }): React.JSX.Element {
  return (
    <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
      <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} />
      <XAxis
        dataKey="step"
        tickLine={false}
        axisLine={false}
        tick={AXIS_TICK}
        tickFormatter={(value: number) => value.toLocaleString()}
      />
      <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} tick={AXIS_TICK} />
      <Tooltip
        content={({ active, payload }) => <StepTooltip active={active} payload={payload} />}
        cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
      />
      <ReferenceLine y={needed} stroke={CHART_REFERENCE_STROKE} strokeDasharray={CHART_REFERENCE_DASH} />
      <Bar dataKey="trainable" stackId="done" fill="hsl(var(--foreground))" isAnimationActive={false} />
      <Bar dataKey="untrainable" stackId="done" fill={CHART_INK} fillOpacity={0.35} isAnimationActive={false} />
    </BarChart>
  )
}

function StepLines({
  rows,
  needed,
  totalSteps,
}: {
  rows: RolloutRow[]
  needed: number
  totalSteps: number
}): React.JSX.Element {
  // One step is one point, which no line can draw: mark it until there are two.
  const single = rows.length === 1
  return (
    <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
      <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
      <XAxis
        dataKey="step"
        type="number"
        domain={[0, totalSteps]}
        ticks={stepTicks(totalSteps)}
        tickLine={false}
        axisLine={false}
        tick={AXIS_TICK}
        tickFormatter={(value: number) => value.toLocaleString()}
      />
      <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} tick={AXIS_TICK} />
      <Tooltip
        content={({ active, payload }) => <StepTooltip active={active} payload={payload} />}
        cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.4)', strokeDasharray: '3 3' }}
      />
      <ReferenceLine y={needed} stroke={CHART_REFERENCE_STROKE} strokeDasharray={CHART_REFERENCE_DASH} />
      <Line
        dataKey={(row: RolloutRow) => row.trainable + row.untrainable}
        name="done"
        stroke={CHART_INK}
        strokeWidth={1}
        dot={single ? { r: 3, fill: CHART_INK, stroke: 'none' } : false}
        isAnimationActive={false}
      />
      <Line
        dataKey="trainable"
        stroke="hsl(var(--foreground))"
        strokeWidth={1.5}
        dot={single ? { r: 3, fill: 'hsl(var(--foreground))', stroke: 'none' } : false}
        isAnimationActive={false}
      />
    </LineChart>
  )
}

export function RolloutProgress({
  run,
  progress,
}: {
  run: TrainingRunDocument
  progress: TrainingRolloutProgress
}): React.JSX.Element {
  const live = liveStep(run, progress)
  const rows = rolloutRows(progress)
  // By the run's planned steps, so one run keeps one form as it grows.
  const bars = run.total_steps <= BAR_STEPS
  return (
    <div className="space-y-6">
      {live !== null && <LiveStep step={live} needed={progress.needed} />}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {(bars ? BAR_LEGEND : LINE_LEGEND).map((entry) => (
            <span
              key={entry.label}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
            >
              {entry.mark}
              {entry.label}
            </span>
          ))}
        </div>
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            {bars ? (
              <StepBars rows={rows} needed={progress.needed} />
            ) : (
              <StepLines rows={rows} needed={progress.needed} totalSteps={run.total_steps} />
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
